import { useEffect, useState } from 'react'
import { useSession } from '../lib/auth-client.ts'
import { Connexion } from './Connexion.tsx'

/**
 * ÉCRAN DE CONSENTEMENT OAuth.
 *
 * C'est une surface de confiance, et elle a ses propres règles — différentes
 * du reste de l'application :
 *
 *  - ZÉRO ANIMATION, décision de principe. Une surface qui demande une
 *    permission ne persuade pas. Seul l'appui reste, parce que c'est un
 *    retour, pas de la persuasion.
 *  - « Autoriser » et « Refuser » ont le MÊME poids visuel. Un écran de
 *    consentement qui pousse visuellement vers l'acceptation est un dark
 *    pattern, même quand on est le seul utilisateur.
 *  - Une liste « ne pourra pas » borne l'octroi. C'est inhabituel, et c'est
 *    le geste honnête : dire ce qu'on donne sans dire ce qu'on ne donne pas
 *    laisse imaginer le pire ou le meilleur.
 *  - Le texte reste SÉLECTIONNABLE (pas de user-select: none) : c'est un
 *    document qu'on doit pouvoir copier et relire.
 *  - Thème clair forcé : cet écran sera capturé et partagé, il doit être
 *    identique pour tout le monde.
 */
export function Autoriser() {
  const { data: session, isPending } = useSession()
  const params = new URLSearchParams(location.search)
  const scope = params.get('scope') ?? ''
  const clientId = params.get('client_id') ?? ''
  /*
   * better-auth ne transmet PAS un code de consentement : il redirige ici
   * avec la requête d'autorisation d'origine, SIGNÉE (paramètres `ba_param`
   * et `sig`). On la lui rend telle quelle dans `oauth_query` — c'est cette
   * signature qui garantit que rien n'a été altéré entre la redirection et
   * la décision, et c'est pourquoi on ne reconstruit surtout pas la chaîne.
   */
  const requeteSignee = location.search

  const [enCours, setEnCours] = useState<'accepte' | 'refuse' | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    document.documentElement.dataset.theme = 'clair'
  }, [])

  async function decider(accept: boolean) {
    setEnCours(accept ? 'accepte' : 'refuse')
    setErreur(null)
    try {
      const r = await fetch('/api/auth/oauth2/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ accept, oauth_query: requeteSignee }),
      })
      /*
       * La réponse est { redirect: true, url }. Les deux autres noms sont des
       * variantes documentées ailleurs dans better-auth : on les tolère pour
       * ne pas casser sur une montée de version, sans en dépendre.
       */
      const data = (await r.json().catch(() => ({}))) as {
        url?: string
        redirectURI?: string
        redirect_uri?: string
        message?: string
      }
      const destination = data.url ?? data.redirectURI ?? data.redirect_uri
      if (!r.ok || !destination) {
        setErreur(data.message ?? "La demande d'autorisation a échoué ou a expiré.")
        setEnCours(null)
        return
      }
      location.href = destination
    } catch {
      setErreur('Impossible de joindre le serveur.')
      setEnCours(null)
    }
  }

  if (isPending) return <div className="min-h-dvh bg-canvas" />
  // Pas connecté : on affiche la connexion. L'URL (donc le `code`) est
  // conservée, la décision reprendra juste après.
  if (!session) return <Connexion />
  if (!params.get('sig')) {
    return (
      <Message
        titre="Demande introuvable"
        texte="Le lien d'autorisation est incomplet ou a expiré. Relance la connexion depuis le client."
      />
    )
  }

  /*
   * On affiche l'HÔTE du client_id, pas un nom que le client s'attribue
   * lui-même. `claude.ai` est vérifiable ; « Claude » ne l'est pas.
   */
  const hote = (() => {
    try {
      return new URL(clientId).host
    } catch {
      return clientId || 'un client inconnu'
    }
  })()

  return (
    <div className="flex min-h-dvh flex-col justify-center bg-canvas px-6 py-10">
      <div className="mx-auto w-full max-w-sm">
        <h1 className="font-serif text-display text-ink">Autoriser l'accès&nbsp;?</h1>
        <p className="mt-2 text-body text-ink-soft">
          <strong className="font-semibold">{hote}</strong> demande à accéder à Popote au nom de{' '}
          <strong className="font-semibold">{session.user.name || session.user.email}</strong>.
        </p>

        <section className="mt-6 rounded-md bg-surface p-4 shadow-row">
          <h2 className="text-label uppercase text-muted">Pourra</h2>
          <ul className="mt-2 flex flex-col gap-1.5 text-body text-ink">
            <li>Lire l'inventaire, la liste de courses, les recettes et le menu</li>
            <li>Modifier l'inventaire et la liste de courses</li>
            <li>Enregistrer des recettes et planifier des repas</li>
            <li>Lire et modifier les goûts et allergies du foyer</li>
          </ul>

          <h2 className="mt-4 text-label uppercase text-muted">Ne pourra pas</h2>
          <ul className="mt-2 flex flex-col gap-1.5 text-body text-muted">
            <li>Supprimer des articles, des recettes ou des comptes</li>
            <li>Accéder aux mots de passe ou aux sessions</li>
            <li>Créer ou supprimer des membres du foyer</li>
          </ul>
        </section>

        <p className="mt-4 text-meta text-muted">
          Portée demandée&nbsp;: <code className="select-all">{scope || 'popote'}</code>
          <br />
          Client&nbsp;: <code className="break-all select-all">{clientId}</code>
        </p>

        {erreur && (
          <p role="alert" className="mt-4 rounded-sm bg-epuise-soft px-3 py-2 text-meta text-epuise-ink">
            {erreur}
          </p>
        )}

        {/* Même poids visuel : ni l'un ni l'autre n'est mis en avant. */}
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            disabled={enCours !== null}
            onClick={() => decider(false)}
            className="pressable h-[52px] flex-1 rounded-sm bg-surface-sunk text-head text-ink disabled:opacity-50"
          >
            {enCours === 'refuse' ? '…' : 'Refuser'}
          </button>
          <button
            type="button"
            disabled={enCours !== null}
            onClick={() => decider(true)}
            className="pressable h-[52px] flex-1 rounded-sm bg-accent text-head text-accent-ink disabled:opacity-50"
          >
            {enCours === 'accepte' ? '…' : 'Autoriser'}
          </button>
        </div>

        <p className="mt-5 text-meta text-muted">
          Tu pourras révoquer cet accès à tout moment depuis les réglages de Popote, ou en
          retirant le connecteur côté {hote}.
        </p>
      </div>
    </div>
  )
}

function Message({ titre, texte }: { titre: string; texte: string }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-8 text-center">
      <h1 className="font-serif text-title text-ink">{titre}</h1>
      <p className="mt-2 text-body text-muted">{texte}</p>
    </div>
  )
}
